import type {
  ChangePlayersStatusRequest,
  ChangePlayersStatusResponse,
  HGChangePlayersStatus,
  HGChangePlayersStatusReply,
  HgGetPlayersBasedOnStatus
} from '@ps/web-shared-api-types/v0/SwHomeGames';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { handleServerResponse, useHomeGamesWidget } from '@swf/poker';

import { ENDPOINTS } from '../constants/endpoints';
import { ModalRoutes } from '../types/common';

interface Request {
  service: string;
  body: ChangePlayersStatusRequest;
}

interface Props {
  action: HGChangePlayersStatus['action'];
  callbackStatuses: HgGetPlayersBasedOnStatus['statuses'];
  userIds: HGChangePlayersStatus['userIds'];
}

const useChangePlayerStatus = (memberType: 'approved' | 'pending' | 'blocked') => {
  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const request = useRequest();
  const { modal } = useDispatch<Dispatch>();
  const { widgetDispatch } = useHomeGamesWidget();

  const handleAction = ({ action, callbackStatuses, userIds }: Props) => {
    request({
      service: ENDPOINTS.changePlayerStatus,
      body: { changePlayersStatus: { action, clubId: Number(clubId), userIds } },
      http: true,
      method: 'PUT'
    } as Request)
      .pipe(catchError((err) => of(err)))
      .subscribe({
        next: ({ body }: { body: ChangePlayersStatusResponse }) => {
          const { error } = handleServerResponse<
            ChangePlayersStatusResponse,
            HGChangePlayersStatusReply
          >({
            response: body,
            accessor: 'changePlayersStatusReply'
          });
          if (error) {
            modal.enqueue({
              path: ModalRoutes.notification,
              locationState: { text: error }
            });
          } else {
            widgetDispatch.fetchClubMembers({
              clubId: Number(clubId),
              statuses: callbackStatuses,
              memberType
            });
            widgetDispatch.fetchClubAvatars({
              clubId: Number(clubId),
              ignoreCache: true
            });
            widgetDispatch.fetchUserClubs();
          }
        }
      });
  };

  return { handleAction };
};

export default useChangePlayerStatus;
